import { useRef, type FC } from 'react';
import {
  NumericAxis,
  NumberRange,
  EAutoRange,
  XyDataSeries,
  XyyDataSeries,
  FastLineRenderableSeries,
  FastBandRenderableSeries,
  NumericLabelProvider,
  NativeTextAnnotation,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  MouseWheelZoomModifier,
  RubberBandXyZoomModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  ZoomExtentsModifier,
  ZoomPanModifier,
  EXyDirection,
} from 'scichart';
import {
  SciChartView,
  AXIS_TITLE_FONT_SIZE,
  AXIS_LABEL_FONT_SIZE,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { formatNumber } from 'utils/number';
import { centerColumnIndex } from 'components/well-page-content/profile-2d-formation-adapter';
import type {
  PreDrillStructureGridProfilesResponse,
  Profile2D,
} from 'types/api-responses';
import {
  addSharedDepthAxes,
  setSharedDepthRange,
  TVDSS_AXIS_ID,
  TVD_AXIS_ID,
} from './subsurface-chart-axes';
import {
  formationBandFill,
  formationBandStroke,
} from './subsurface-formation-style';
import {
  buildProfileFormationBands,
  buildProfileTypeCurveBands,
  buildTypeCurveFormationLabels,
  profile2dGroundDatumFt,
} from './utils';

type ProfileResult = PreDrillStructureGridProfilesResponse['results'][number];

interface PreDrillProfile2DChartProps {
  result: ProfileResult | null;
  /** Subject surface→toe trajectory (VS vs TVDSS); drives the well line when
   *  present, else the structure-grid trajectory is the fallback. */
  wellProfile2D?: Profile2D | null;
  tvdssDomain: [number, number] | null;
  isLoading: boolean;
  /** Subject bore color (Well Qualifier rule); defaults to near-black. */
  subjectColor?: string;
  /** The geometry tearsheet's controls, matching the Cross-Section beside it: drag pans,
   *  the wheel zooms (the right TVDSS axis included), dragging an axis rescales just that
   *  axis, double-click fits. */
  sectionControls?: boolean;
}

const DEFAULT_SUBJECT = '#161616';
const fmt = (v: number) => formatNumber(v, 0, 0);

// 2D Profile (vertical section): subject bore trajectory (VS vs TVDSS) over
// formation bands, sharing the Gun Barrel's TVDSS/TVD axes so gridlines align.
export const PreDrillProfile2DChart: FC<PreDrillProfile2DChartProps> = ({
  result,
  wellProfile2D = null,
  tvdssDomain,
  isLoading,
  subjectColor = DEFAULT_SUBJECT,
  sectionControls = false,
}) => {
  const stateRef = useRef({ result, wellProfile2D, tvdssDomain, subjectColor });
  stateRef.current = { result, wellProfile2D, tvdssDomain, subjectColor };
  const elevRef = useRef(0);

  const initChart = ({ surface, wasmContext }: SciChartInitContext) => {
    const xLabel = new NumericLabelProvider();
    xLabel.formatLabel = fmt;
    surface.xAxes.add(
      new NumericAxis(wasmContext, {
        axisTitle: 'Vertical Section (ft)',
        axisTitleStyle: { fontSize: AXIS_TITLE_FONT_SIZE },
        labelStyle: { fontSize: AXIS_LABEL_FONT_SIZE },
        autoRange: EAutoRange.Never,
        drawMinorGridLines: false,
        labelProvider: xLabel,
      })
    );
    addSharedDepthAxes(surface, wasmContext, elevRef, {
      mirrorDrivesPrimary: sectionControls,
    });

    if (sectionControls) {
      // The mirror has no series, so a fit would size it to nothing and hand that back;
      // the fit sizes the primary and the mirror follows.
      const fit = new ZoomExtentsModifier();
      fit.includeYAxis(surface.yAxes.getById(TVD_AXIS_ID), false);
      // Dragging a gutter rescales just that axis, as the Inventory gun barrel chart does.
      // It happens outside the series rect, so it costs the plot-area pan nothing.
      surface.chartModifiers.add(
        new ZoomPanModifier(),
        new MouseWheelZoomModifier(),
        new XAxisDragModifier(),
        new YAxisDragModifier(),
        fit
      );
      return;
    }

    // Y-axis (depth) zoom only — the lateral (X) axis stays fixed.
    surface.chartModifiers.add(
      new RubberBandXyZoomModifier({ xyDirection: EXyDirection.YDirection })
    );
    surface.chartModifiers.add(
      new MouseWheelZoomModifier({ xyDirection: EXyDirection.YDirection })
    );
    surface.chartModifiers.add(new YAxisDragModifier());
  };

  const renderData = ({ surface, wasmContext }: SciChartInitContext) => {
    surface.renderableSeries.clear();
    surface.annotations.clear();
    const {
      result: model,
      wellProfile2D: profile,
      tvdssDomain: domain,
      subjectColor: color,
    } = stateRef.current;
    // A bore with no structure grid under it gets no grid profile at all (UAT 30),
    // but its own line is still known from the wells query: draw that alone, with
    // no bands and no labels, rather than an empty plot.
    if (!model && !profile) return;

    // Subject well line: prefer `wellboreTrajectory` (VS↔TVDSS, index-aligned
    // with the formations), else fall back to profile2D when it's absent.
    const sgTraj = model?.wellboreTrajectory;
    const trajVs = sgTraj?.vsValues ?? [];
    const trajTvdss = sgTraj?.tvdssValues ?? [];
    const hasTraj = trajVs.length > 0 && trajVs.length === trajTvdss.length;
    const profileTraj = profile?.trajectory ?? [];
    const lineVs = hasTraj ? trajVs : profileTraj.map((p) => p[0]);
    const lineTvdss = hasTraj ? trajTvdss : profileTraj.map((p) => p[1]);

    // Ground-level (KB) datum for the left TVD axis. TVDSS + TVD is invariant
    // along the bore, so the first sample recovers the surface elevation; the
    // axis then labels TVD = datum − TVDSS. Without this the datum stays 0 and
    // the TVD axis reads identically to TVDSS.
    const trajTvd = sgTraj?.tvdValues ?? [];
    const profileTvd = profile?.tvdValues ?? [];
    const datum = profile2dGroundDatumFt(
      hasTraj ? trajTvdss : [],
      hasTraj ? trajTvd : [],
      profileTraj.map((p) => p[1]),
      profileTvd
    );
    if (datum !== null) elevRef.current = datum;
    // Per-eval-point Vertical Section (the X formations plot against). Each
    // formation row is one eval point (formations grid is [evalPoint][slice]),
    // so it must sit at that eval point's own `vsFt` — NOT the wellbore's
    // denser, differently-sampled `vsValues`, which would drift the bands off
    // the lateral on a dipping formation. The well line stays on `trajVs`.
    const evalVs = model?.evalPoints?.map((ep) => ep.vsFt ?? 0) ?? [];
    // A bore profiled by its address (the sheet's bore with no geometry row) and
    // drilled vertically has no eval points: its formations are one row, sampled at
    // the surface hole, so that row sits at VS 0 and the padding carries it across.
    const sgVs =
      evalVs.length === 0 && model?.subjectKind === 'well' ? [0] : evalVs;
    // Center slice column: formation grid is [evalPoint][slice]; read this
    // column for one depth per eval point.
    const centerSlice = centerColumnIndex(model?.slicePositions ?? []);

    // Vertical Section axis spans the well line + the formation eval points.
    const allVs = [...lineVs, ...sgVs];
    const maxVs = allVs.length > 0 ? Math.max(...allVs) : 10000;
    // A filed survey can open a few feet behind its surface hole, and a vertical bore
    // lives in those few feet, so a bore profiled by its address starts the axis there.
    const minVs =
      model?.subjectKind === 'well' && allVs.length > 0
        ? Math.min(0, ...allVs)
        : 0;
    const bandX = [minVs, maxVs * 1.05];

    // Pad each band's first/last sample out to the axis edges so the bands span
    // the full VS width (matching the landing chart's gun-barrel-formation-series
    // padding), instead of stopping at the eval-point/lateral extent. Repeats the
    // nearest depth — no interpolation; depths at real eval points are unchanged.
    const padBandToAxis = <
      B extends { xs: number[]; tops: number[]; bases: number[] },
    >(
      band: B
    ): B => {
      const xs = [...band.xs];
      const tops = [...band.tops];
      const bases = [...band.bases];
      if (xs.length > 0) {
        if (xs[0] > bandX[0]) {
          xs.unshift(bandX[0]);
          tops.unshift(tops[0]);
          bases.unshift(bases[0]);
        }
        const last = xs.length - 1;
        if (xs[last] < bandX[1]) {
          xs.push(bandX[1]);
          tops.push(tops[last]);
          bases.push(bases[last]);
        }
      }
      return { ...band, xs, tops, bases };
    };

    // Type-curve formations → filled bands, drawn first so SG top lines sit on
    // top of them.
    buildProfileTypeCurveBands(
      model?.typeCurveFormations ?? [],
      sgVs,
      centerSlice
    ).forEach((band) => {
      const b = padBandToAxis(band);
      surface.renderableSeries.add(
        new FastBandRenderableSeries(wasmContext, {
          yAxisId: TVDSS_AXIS_ID,
          dataSeries: new XyyDataSeries(wasmContext, {
            xValues: b.xs,
            yValues: b.tops,
            y1Values: b.bases,
          }),
          fill: formationBandFill(band.color, band.name),
          fillY1: formationBandFill(band.color, band.name),
          stroke: formationBandStroke(band.color),
          strokeY1: formationBandStroke(band.color),
          strokeThickness: 1,
        })
      );
    });

    // Structure-grid formations → filled bands (top→base per eval point), each
    // with a right-edge geologic-interval label at the band's mid-depth.
    buildProfileFormationBands(
      model?.formations ?? [],
      sgVs,
      centerSlice
    ).forEach((band) => {
      const b = padBandToAxis(band);
      surface.renderableSeries.add(
        new FastBandRenderableSeries(wasmContext, {
          yAxisId: TVDSS_AXIS_ID,
          dataSeries: new XyyDataSeries(wasmContext, {
            xValues: b.xs,
            yValues: b.tops,
            y1Values: b.bases,
          }),
          fill: formationBandFill(band.color, band.name),
          fillY1: formationBandFill(band.color, band.name),
          stroke: formationBandStroke(band.color),
          strokeY1: formationBandStroke(band.color),
          strokeThickness: 1,
        })
      );
      // Label at the band mid-depth of the last eval point with both bounds.
      let lastIdx = -1;
      for (let i = band.tops.length - 1; i >= 0; i -= 1) {
        if (Number.isFinite(band.tops[i]) && Number.isFinite(band.bases[i])) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx >= 0) {
        surface.annotations.add(
          new NativeTextAnnotation({
            x1: 1,
            y1: (band.tops[lastIdx] + band.bases[lastIdx]) / 2,
            xCoordinateMode: ECoordinateMode.Relative,
            yAxisId: TVDSS_AXIS_ID,
            text: band.geologicInterval ?? band.name,
            fontSize: 8,
            textColor: '#161616',
            horizontalAnchorPoint: EHorizontalAnchorPoint.Right,
            verticalAnchorPoint: EVerticalAnchorPoint.Center,
          })
        );
      }
    });

    // Type-curve formation names → left-margin labels at their mid-depth.
    buildTypeCurveFormationLabels(model?.typeCurveFormations ?? []).forEach(
      (label) => {
        surface.annotations.add(
          new NativeTextAnnotation({
            x1: 0,
            y1: label.midTvdss,
            xCoordinateMode: ECoordinateMode.Relative,
            yAxisId: TVDSS_AXIS_ID,
            text: label.name,
            fontSize: 9,
            textColor: label.color,
            horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
            verticalAnchorPoint: EVerticalAnchorPoint.Center,
          })
        );
      }
    );

    // Subject well line in trajectory order. The path can double back in VS, so
    // it's flagged unsorted — SciChart connects points in index order.
    if (lineVs.length > 0 && lineVs.length === lineTvdss.length) {
      surface.renderableSeries.add(
        new FastLineRenderableSeries(wasmContext, {
          yAxisId: TVDSS_AXIS_ID,
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: lineVs,
            yValues: lineTvdss,
            dataIsSortedInX: false,
          }),
          stroke: color,
          strokeThickness: 3,
        })
      );
    }

    surface.xAxes.get(0).visibleRange = new NumberRange(bandX[0], bandX[1]);
    setSharedDepthRange(surface, domain);
  };

  return (
    <SciChartView
      initChart={initChart}
      renderData={renderData}
      renderDeps={[result, wellProfile2D, tvdssDomain, subjectColor]}
      isLoading={isLoading}
      showResetZoom
      height="100%"
      exportConfig={{
        label: '2D Profile',
        filenamePrefix: 'pre-drill-2d-profile',
      }}
    />
  );
};
